import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressBook,
  faBuilding,
  faEnvelope,
  faPhone,
  faRotate,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { getlist } from "../../contexts/apicontexts";
import config from "../../config";
import { getCurrentUserIdentity } from "../Tickets/ticketUtils";

const PAGE_SIZE = 10;

const normalizeCustomer = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? ""),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? ""),
  email: String(item?.email ?? item?.Email ?? ""),
  mobile: String(item?.mobile ?? item?.Mobile ?? ""),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? ""),
  domain: String(item?.domain ?? item?.Domain ?? ""),
  address: String(item?.address ?? item?.Address ?? ""),
  isCurrentCustomer: Boolean(item?.isCurrentCustomer ?? item?.IsCurrentCustomer),
  agreementStartDate: item?.agreementStartDate ?? item?.AgreementStartDate ?? "",
  agreementEndDate: item?.agreementEndDate ?? item?.AgreementEndDate ?? "",
  empUserId: String(item?.empUserId ?? item?.EmpUserId ?? ""),
});

const formatDate = (value) => {
  if (!value) return "Not set";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export default function MyCustomersPage() {
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadCustomers = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getlist(`${config.baseurl}Customer/getmylist`);
      if (response?.status === 200) {
        setCustomers(Array.isArray(response.data) ? response.data.map(normalizeCustomer) : []);
      } else {
        setError("Assigned customer list could not be loaded.");
      }
    } catch {
      setError("Assigned customer list could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return customers;

    return customers.filter((item) =>
      [
        item.name,
        item.customerCode,
        item.email,
        item.mobile,
        item.organizationName,
        item.domain,
        item.address,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [customers, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const paginatedCustomers = useMemo(
    () => filteredCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredCustomers, page]
  );

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const activeCount = customers.filter((item) => item.isCurrentCustomer).length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="My Customers" currentPage="My Customers" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.20),_transparent_30%),linear-gradient(135deg,#0f172a,#1e293b_58%,#334155)] px-5 py-4 text-white md:px-6">
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-3 text-xs text-slate-200">
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                {identity.fullName || "User"}
              </span>
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                Email: {identity.email || "Unavailable"}
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <StatCard label="Assigned Customers" value={customers.length} icon={faAddressBook} />
              <StatCard label="Active" value={activeCount} icon={faBuilding} />
            </div>
          </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50/80 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full min-w-[260px]">
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search assigned customers"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                />
              </div>

              <button
                type="button"
                onClick={loadCustomers}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 md:p-6">
          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center text-sm text-slate-500">
              <div className="mb-3 flex justify-center">
                <DotLoader color="#0f172a" size="w-1.5 h-1.5" />
              </div>
              Loading your assigned customers...
            </div>
          ) : paginatedCustomers.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <thead className="bg-slate-100 text-left text-xs uppercase tracking-[0.18em] text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Customer</th>
                      <th className="px-4 py-3">Organization</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Mobile</th>
                      <th className="px-4 py-3">Agreement</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCustomers.map((item) => (
                      <tr key={item.id} className="border-t border-slate-200 align-top hover:bg-slate-50/70">
                        <td className="px-4 py-4">
                          <div className="font-semibold text-slate-900">{item.name || "Unnamed customer"}</div>
                          <div className="mt-1 text-xs font-medium text-slate-500">{item.customerCode || "No code"}</div>
                          <div className="mt-2 text-xs text-slate-500">{item.domain || "No domain"}</div>
                          {item.address ? <div className="mt-1 max-w-sm text-xs text-slate-400">{item.address}</div> : null}
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <div className="inline-flex items-center gap-2">
                            <FontAwesomeIcon icon={faBuilding} className="text-slate-400" />
                            <span>{item.organizationName || "No organization"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <div className="inline-flex items-center gap-2">
                            <FontAwesomeIcon icon={faEnvelope} className="text-slate-400" />
                            <span>{item.email || "No email"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <div className="inline-flex items-center gap-2">
                            <FontAwesomeIcon icon={faPhone} className="text-slate-400" />
                            <span>{item.mobile || "No mobile"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <div>Start: {formatDate(item.agreementStartDate)}</div>
                          <div className="mt-1 text-xs text-slate-500">End: {formatDate(item.agreementEndDate)}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isCurrentCustomer
                              ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border border-slate-200 bg-slate-100 text-slate-600"
                              }`}
                          >
                            {item.isCurrentCustomer ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredCustomers.length > PAGE_SIZE ? (
                <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-500">
                    Showing {paginatedCustomers.length} of {filteredCustomers.length} customers
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={page === 1}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                      disabled={page >= totalPages}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <FontAwesomeIcon icon={faAddressBook} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No assigned customers found</h3>
              <p className="mt-2 text-sm text-slate-500">
                No customer record is currently mapped to your login user ID through the <span className="font-medium">EmpUserId</span> field.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-200">{label}</div>
          <div className="mt-1 text-3xl font-semibold text-white">{value}</div>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white">
          <FontAwesomeIcon icon={icon} />
        </div>
      </div>
    </div>
  );
}
